import { useEffect, useState, useRef, useCallback } from "react";
import { api, BACKEND_URL } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/i18n";
import { PageHeader, EmptyState, downloadStoredFile } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ConfirmDelete } from "@/components/ConfirmDelete";
import { Plus, Pill, UploadSimple, MapPin, PencilSimple, MagnifyingGlass, DownloadSimple, Trash, LinkSimple } from "@phosphor-icons/react";

function ItemImg({ path, full = false, onClick, testid }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    if (!path) return;
    let url;
    let cancelled = false;
    const token = localStorage.getItem("token");
    fetch(`${BACKEND_URL}/api/files/${path}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob())
      .then((b) => { if (cancelled) return; url = URL.createObjectURL(b); setSrc(url); })
      .catch(() => {});
    return () => { cancelled = true; if (url) URL.revokeObjectURL(url); };
  }, [path]);
  if (!path) return (
    <div className="aspect-square bg-muted flex items-center justify-center">
      <Pill size={40} className="text-zinc-300" weight="duotone" />
    </div>
  );
  if (!src) return <div className={full ? "h-64 bg-muted animate-pulse rounded-md" : "aspect-square bg-muted animate-pulse"} />;
  if (full) return <img src={src} alt="Product" className="max-h-[70vh] w-full object-contain rounded-md bg-muted" data-testid={testid} />;
  return (
    <img
      src={src}
      alt="Product"
      onClick={onClick}
      data-testid={testid}
      className={`aspect-square w-full object-cover${onClick ? " cursor-zoom-in transition-transform hover:scale-[1.03]" : ""}`}
    />
  );
}

const empty = { name: "", origin: "", note: "" };

export default function Catalogue() {
  const { isTeam } = useAuth();
  const { t } = useT();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [search, setSearch] = useState("");
  const [zoomItem, setZoomItem] = useState(null);
  const photoRefs = useRef({});

  const load = useCallback(() => {
    api.get("/catalogue").then((r) => setItems(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const q = search.trim().toLowerCase();
  const shown = q ? items.filter((it) => (it.name || "").toLowerCase().includes(q)) : items;

  const openNew = () => { setForm(empty); setEditId(null); setOpen(true); };
  const openEdit = (it) => { setForm({ name: it.name, origin: it.origin || "", note: it.note || "" }); setEditId(it.id); setOpen(true); };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Product name is required");
    setBusy(true);
    try {
      if (editId) {
        await api.patch(`/catalogue/${editId}`, form);
        toast.success("Product updated");
      } else {
        await api.post("/catalogue", form);
        toast.success("Product added");
      }
      setOpen(false);
      setForm(empty);
      setEditId(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id, name) => {
    await api.delete(`/catalogue/${id}`);
    toast.success(`${name} removed`);
    load();
  };

  const uploadPhoto = async (id, e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    try {
      const fd = new FormData();
      fd.append("file", f);
      await api.post(`/catalogue/${id}/photo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Photo updated");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      if (photoRefs.current[id]) photoRefs.current[id].value = "";
    }
  };

  const removePhoto = async (id) => {
    if (!window.confirm("Remove this photo?")) return;
    try { await api.delete(`/catalogue/${id}/photo`); toast.success("Photo removed"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed to remove"); }
  };

  const copyPublicLink = () => {
    const url = `${window.location.origin}/c/heliopolis`;
    navigator.clipboard.writeText(url).then(
      () => toast.success("Public catalogue link copied"),
      () => toast.error("Could not copy link")
    );
  };

  return (
    <div className="space-y-6">
      <PageHeader
        subtitle={t("cat.subtitle")}
        title={t("cat.title")}
        actions={isTeam && (
          <div className="flex items-center gap-2">
            <Button variant="outline" className="rounded-sm" onClick={copyPublicLink} data-testid="copy-public-catalogue-link"><LinkSimple size={16} className="mr-2" /> Copy public link</Button>
            <Button className="rounded-sm" onClick={openNew} data-testid="add-catalogue-item"><Plus size={16} className="mr-2" /> Add Product</Button>
          </div>
        )}
      />

      {items.length > 0 && (
        <div className="relative max-w-md">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input className="pl-9 rounded-sm" placeholder={t("cat.searchPh")} value={search} onChange={(e) => setSearch(e.target.value)} data-testid="catalogue-search" />
        </div>
      )}

      {!loading && items.length === 0 ? (
        <EmptyState
          title={isTeam ? "No products yet" : t("cat.noneTitle")}
          hint={isTeam ? "Add products with a photo, origin and a short note for your clients to browse." : t("cat.noneHintClient")}
          action={isTeam && <Button className="rounded-sm" onClick={openNew}>Add Product</Button>}
        />
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
          {shown.map((it) => (
            <div key={it.id} className="hp-card overflow-hidden flex flex-col" data-testid={`catalogue-card-${it.name}`}>
              <div className="relative">
                <ItemImg path={it.photo} onClick={it.photo ? () => setZoomItem(it) : undefined} testid={`catalogue-photo-${it.name}`} />
                {it.photo && (
                  <button
                    onClick={() => downloadStoredFile(it.photo, `${it.name}.jpg`)}
                    className="absolute bottom-2 left-2 inline-flex items-center gap-1 rounded-full bg-black/60 text-white text-xs px-2.5 py-1 hover:bg-black/80 transition-colors"
                    data-testid={`download-catalogue-photo-${it.name}`}
                  >
                    <DownloadSimple size={12} /> {t("cat.download")}
                  </button>
                )}
                {isTeam && (
                  <>
                    <button
                      onClick={() => photoRefs.current[it.id]?.click()}
                      className="absolute bottom-2 right-2 inline-flex items-center gap-1 rounded-full bg-black/60 text-white text-xs px-2.5 py-1 hover:bg-black/80 transition-colors"
                      data-testid={`upload-catalogue-photo-${it.name}`}
                    >
                      <UploadSimple size={12} /> {it.photo ? "Change" : "Add photo"}
                    </button>
                    <input ref={(el) => (photoRefs.current[it.id] = el)} type="file" accept="image/*" className="hidden" onChange={(e) => uploadPhoto(it.id, e)} />
                    {it.photo && (
                      <button
                        onClick={() => removePhoto(it.id)}
                        className="absolute top-2 right-2 inline-flex items-center gap-1 rounded-full bg-black/60 text-white text-xs px-2.5 py-1 hover:bg-red-600 transition-colors"
                        data-testid={`remove-catalogue-photo-${it.name}`}
                      >
                        <Trash size={12} /> Remove
                      </button>
                    )}
                  </>
                )}
              </div>
              <div className="p-4 flex-1 flex flex-col">
                <p className="font-heading font-bold">{it.name}</p>
                {it.origin && (
                  <p className="text-xs text-muted-foreground flex items-center gap-1 mt-1"><MapPin size={12} /> {t("cat.origin")}: {it.origin}</p>
                )}
                {it.note && <p className="text-sm text-muted-foreground mt-2 flex-1">{it.note}</p>}
                {isTeam && (
                  <div className="flex items-center gap-2 mt-3 pt-3 border-t border-border">
                    <Button variant="outline" size="sm" className="rounded-sm flex-1" onClick={() => openEdit(it)} data-testid={`edit-catalogue-${it.name}`}>
                      <PencilSimple size={14} className="mr-1.5" /> Edit
                    </Button>
                    <ConfirmDelete
                      testid={`delete-catalogue-${it.name}`}
                      title={`Remove ${it.name}?`}
                      description="This permanently removes the product from the catalogue."
                      onConfirm={() => remove(it.id, it.name)}
                    />
                  </div>
                )}
              </div>
            </div>
          ))}
          {shown.length === 0 && (
            <div className="col-span-full py-10 text-center text-muted-foreground" data-testid="catalogue-no-results">No products match "{search}".</div>
          )}
        </div>
      )}

      <Dialog open={!!zoomItem} onOpenChange={(o) => !o && setZoomItem(null)}>
        <DialogContent className="max-w-3xl" data-testid="catalogue-photo-dialog">
          <DialogHeader><DialogTitle>{zoomItem?.name}</DialogTitle></DialogHeader>
          {zoomItem && <ItemImg path={zoomItem.photo} full testid="catalogue-photo-zoom-img" />}
          {zoomItem?.origin && <p className="text-sm text-muted-foreground flex items-center gap-1.5"><MapPin size={14} /> {zoomItem.origin}</p>}
          {zoomItem?.note && <p className="text-sm text-muted-foreground">{zoomItem.note}</p>}
          {zoomItem?.photo && (
            <DialogFooter>
              <Button variant="outline" className="rounded-sm" onClick={() => downloadStoredFile(zoomItem.photo, `${zoomItem.name}.jpg`)} data-testid="catalogue-photo-download"><DownloadSimple size={16} className="mr-2" /> Download</Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editId ? "Edit Product" : "Add Product"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs uppercase tracking-wide">Product name *</Label>
              <Input className="mt-1.5 rounded-sm" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Paracetamol 500mg" data-testid="catalogue-name" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide">Origin (optional)</Label>
              <Input className="mt-1.5 rounded-sm" value={form.origin} onChange={(e) => setForm({ ...form, origin: e.target.value })} placeholder="e.g. Germany" data-testid="catalogue-origin" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide">Note (optional)</Label>
              <Textarea rows={3} className="mt-1.5 rounded-sm" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="Short product description, strength, packaging…" data-testid="catalogue-note" />
            </div>
            <p className="text-xs text-muted-foreground">You can add a photo from the product card after saving.</p>
          </div>
          <DialogFooter><Button className="rounded-sm" onClick={save} disabled={busy} data-testid="save-catalogue-item">{busy ? "Saving…" : "Save"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
